/// <amd-module name="TFS/Services/TfsProjectService" />
import { ContributedFeature } from "TFS/FeatureManagement/Contracts/FeatureManagement";
import { IObservableArray, IReadyableReadonlyObservableArray, ObservableArray, ReadyableObservableArray } from "VSS/Core/Observable";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { routeUrl } from "VSS/Platform/Location";

/**
 * Represents a single team project.
 */
export interface ITeamProject {
    id: string;
    imageUrl?: string;
    name: string;
    url: string;
    description: string;
    products: ContributedFeature[];
}

export interface IProjectItem {
    projectName: string;
    projectId: string;
    projectImageUrl?: string;
    projectDescription: string;
    products: ContributedFeature[];
}

export interface IProjectData {
    projects: IProjectItem[];
}

/**
 * Service for working with team projects
 */
export interface ITfsProjectService extends IVssService {
    /**
     * Get all team projects in the host collection
     */
    getAllProjects(): IReadyableReadonlyObservableArray<ITeamProject>;

    /**
     * Get most recently used team projects for a given user,
     * within the current collection
     */
    getMruProjects(): IObservableArray<ITeamProject>;

    /**
     * Gets a promise that resolves when the ObservableArray returned by getAllProjects() is ready.
     */
    getAllProjectsLoadedPromise(): Promise<ITeamProject[]>;
}

const projectDataProvider = "ms.vss-tfs-web.project-plus-product-data-provider";
const mruDataProvider = "ms.vss-tfs-web.project-mru-data-provider";

class TfsProjectService extends VssService implements ITfsProjectService {
    private allProjects: ReadyableObservableArray<ITeamProject>;
    private mruProjects: ObservableArray<ITeamProject>;
    private allProjectsPromise: Promise<ITeamProject[]> | undefined;

    public getAllProjects(): IReadyableReadonlyObservableArray<ITeamProject> {
        if (!this.allProjects) {
            this.allProjects = new ReadyableObservableArray();

            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const projectData = contributionService.getData<IProjectData>(projectDataProvider);
            if (projectData) {
                this.allProjects.push(...this.getProjectsFromProjectData(projectData));
                this.allProjects.ready.value = true;
                this.allProjectsPromise = Promise.resolve(this.allProjects.value);
            } else {
                this.allProjectsPromise = contributionService.getDataAsync<IProjectData>(projectDataProvider).then((projectData) => {
                    this.allProjects.push(...this.getProjectsFromProjectData(projectData));
                    this.allProjects.ready.value = true;
                    return this.allProjects.value;
                });
            }
        }
        return this.allProjects;
    }

    public getAllProjectsLoadedPromise(): Promise<ITeamProject[]> {
        if (!this.allProjectsPromise) {
            this.getAllProjects();
        }
        return this.allProjectsPromise!;
    }

    public getMruProjects(): ObservableArray<ITeamProject> {
        if (!this.mruProjects) {
            this.mruProjects = new ObservableArray([]);

            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const mruData = contributionService.getData<IProjectData>(mruDataProvider);
            if (mruData) {
                this.mruProjects.push(...this.getProjectsFromProjectData(mruData));
            } else {
                contributionService.getDataAsync<IProjectData>(mruDataProvider).then((mruData) => {
                    this.mruProjects.push(...this.getProjectsFromProjectData(mruData));
                });
            }
        }
        return this.mruProjects;
    }

    private getProjectsFromProjectData(data: IProjectData | undefined): ITeamProject[] {
        let teamProjects: ITeamProject[] = [];

        if (data) {
            teamProjects = data.projects.map((project) => {
                const url = routeUrl(this.pageContext, "ms.vss-tfs-web.project-overview-route", { project: project.projectName });
                return {
                    id: project.projectId,
                    imageUrl: project.projectImageUrl,
                    name: project.projectName,
                    url: url,
                    description: project.projectDescription,
                    products: project.products
                };
            });
        }

        return teamProjects;
    }
}

Services.add("ITfsProjectService", { serviceFactory: TfsProjectService });
